// Блок «Телефон» во вкладке «Доступ»: какие телефоны у человека и доходят ли до них уведомления.
//
// ## Зачем он появился
//
// 28-30.09.2026 заказчик впервые проверил приложения: на заблокированный телефон уведомления не
// приходили, а причину («не разрешили», «нет ключа», «адрес от другой среды», «приложение
// переустановили») не видел никто, кроме сервера. Теперь сервер умеет пробовать и отвечает по
// каждому телефону словами. Задача блока — чтобы за минуту стало понятно, почему у сотрудника
// молчит телефон, и что ему нажать.
//
// ## Что здесь НЕ выдумывается
//
// Слова итога — `summary`, `message`, `next_action` — приходят с сервера готовыми и показываются
// как есть. Свои причины экран не сочиняет: в списке из десяти исходов он знает только, какой из
// них успех, какой требует внимания и какой — ошибка, и красит по этому.
//
// ## Решения вне буквы задания, и каждое по прочтению кода, а не по вкусу
//
// **«Сообщил адрес», а не «заходил».** Сервер двигает `last_seen_at` только тогда, когда телефон
// присылает свой адрес, а стоящие у людей версии приложения один раз отправленный адрес повторно
// не шлют (`PushRegistrar.offer`). «Заходил 28 сентября» про человека, который открывает
// приложение каждый день, — неправда, и администратор по ней сделал бы вывод о сотруднике, а не
// о телефоне; к тому же настоящий «последний вход» человека уже стоит строкой выше, в поле
// «Последний вход» этой же вкладки. «Сообщил адрес 28 сентября» верно всегда и полезно: оно
// расходится со словами «я переустановила в понедельник» ровно тогда, когда новый адрес до
// сервера не дошёл.
//
// **Строка телефона не спорит с пробой.** «Принимает уведомления» — это всё, что сервер знает до
// пробы: адрес есть и не отцеплён. Проба может показать обратное (приложение переустановили,
// сервер и сборка в разных средах), и строка, продолжающая писать «принимает» над красным
// ответом, врала бы в самом важном месте экрана. После пробы строка, у которой проба не
// прошла, говорит об этом сама и отсылает к ответу ниже.
//
// **Одно и то же не говорится дважды.** У единственного телефона общая строка итога
// («Отправлено на 1 устройство») пересказывала бы его же плашку, и не пересказывается. Когда все
// телефоны отцеплены, объяснение «слать некуда» стоит под списком один раз, а в строках остаётся
// короткое «Адрес отцеплён». «Что делать», совпавшее со словами плашки, не повторяется.
//
// **Занятая кнопка остаётся кнопкой.** Выключенная (`disabled`) в Chromium, то есть в движке
// десктопа на Windows, отдаёт фокус странице: после ответа человек с клавиатуры оказывается в
// начале вкладки. Второй запрос отсекает сам запуск пробы, а вид «занято» даёт `aria-disabled`.

import type { RemixiconComponentType } from '@remixicon/react';
import {
  RiAlertLine,
  RiAndroidFill,
  RiAppleFill,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiGlobalLine,
  RiInformationLine,
  RiNotification3Line,
  RiSmartphoneLine,
} from '@remixicon/react';

import type { DeviceOut, DeviceTestItem, DeviceTestResult } from '@/shared/api/endpoints/devices';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { formatDataAsOf, formatTimeHM, parseMoment } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { cn } from '@/utils/cn';

import type { УдачнаяПроба } from '../access/телефон';
import { useПробаУведомлений, useУстройстваЧеловека } from '../access/телефон';

/** Видеть телефоны человека. То же право, что у самой вкладки «Доступ». */
const ПРАВО_СМОТРЕТЬ = 'core.user.view';
/** Право проверять: то же, что у раздела «Доступ» — проверять доставку ведёт тот, кто впустил. */
const ПРАВО_МЕНЯТЬ = 'core.user.manage';
const ПРАВО_ДОСТУПА = 'core.user.access';

/**
 * Блок одного человека. Ключ по человеку стоит ЗДЕСЬ, а не у вызывающего: проба, её ход и итог
 * принадлежат одному человеку, и карточка, перелистнувшая на соседа без перемонтирования,
 * показала бы ему «Отправляем…» и чужой итог. Обвязка защищает от этого всех, кто блок позовёт.
 */
export function PhoneBlock({ userId }: { userId: string }) {
  return <ТелефонЧеловека key={userId} userId={userId} />;
}

function ТелефонЧеловека({ userId }: { userId: string }) {
  const права = useAuthStore((состояние) => состояние.permissions);
  const можноСмотреть = can(права, ПРАВО_СМОТРЕТЬ);
  const можноПробовать = can(права, ПРАВО_МЕНЯТЬ) || can(права, ПРАВО_ДОСТУПА);

  // Запрос без права не уходит вовсе: «нельзя» лучше сказано не отправленным запросом, чем
  // отказом сервера, которого никто не просил.
  const устройства = useУстройстваЧеловека(userId, можноСмотреть);
  const проба = useПробаУведомлений(userId);

  // Блока нет вовсе, как нет и вкладки: не пустой и не серый, а не нарисованный.
  if (!можноСмотреть) {
    return null;
  }

  // Отметка времени среза — как везде в продукте (правило 11). Данные переживают плохую сеть:
  // пока свежие не приехали, стоят последние известные, и по этой строке видно, насколько они
  // давние.
  const срез =
    устройства.dataUpdatedAt > 0 ? formatDataAsOf(new Date(устройства.dataUpdatedAt)) : undefined;

  return (
    <Block
      icon={RiSmartphoneLine}
      title='Телефон'
      // Пока список едет, на месте отметки стоит её каркас: иначе шапка, приняв отметку,
      // вырастала бы на строку, и всё под ней подпрыгивало.
      note={
        срез ?? (устройства.isPending ? <Skeleton className='inline-block h-3 w-24' /> : undefined)
      }
    >
      <ScreenState
        data={устройства.data}
        error={устройства.error}
        subject='телефоны человека'
        isEmpty={(список) => список.length === 0}
        onRetry={() => void устройства.refetch()}
        loading={<СкелетБлока />}
        empty={<НетТелефонов />}
      >
        {(список) => <СТелефонами список={список} можноПробовать={можноПробовать} проба={проба} />}
      </ScreenState>
    </Block>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════
// Состояния блока
// ══════════════════════════════════════════════════════════════════════════════════════════

/**
 * Скелет ПОХОЖ на то, что приедет: значок и три строки телефона, под ними кнопка. Высота
 * близка к блоку с одним телефоном, поэтому содержимое не подпрыгивает, когда ответ приходит.
 * Слова «телефонов нет» на этом месте не пишутся: пока список едет, ответа мы не знаем, а
 * написанное читается как ответ — и человек идёт говорить сотруднику нажимать «Разрешить».
 */
function СкелетБлока() {
  return (
    <div className='flex flex-col gap-3'>
      <div className='flex gap-3'>
        <Skeleton className='size-5 shrink-0' />
        <div className='flex min-w-0 flex-1 flex-col gap-2'>
          <Skeleton className='h-4 w-1/3' />
          <Skeleton className='h-3 w-2/3' />
          <Skeleton className='h-4 w-1/2' />
        </div>
      </div>
      <Skeleton className='h-8 w-44' />
    </div>
  );
}

/** Телефон не сообщал адреса вовсе: причина и следующий шаг, а не белое поле (правило 7). */
function НетТелефонов() {
  return (
    <div className='flex flex-col gap-1'>
      <p className='text-label-sm text-text-strong-950'>
        Телефон ещё не сообщил «Барку» свой адрес
      </p>
      <p className='text-paragraph-sm text-text-sub-600'>
        Человеку нужно открыть «Барк» на телефоне, нажать на колокольчик («Уведомления») и
        «Разрешить».
      </p>
    </div>
  );
}

function СТелефонами({
  список,
  можноПробовать,
  проба,
}: {
  список: DeviceOut[];
  можноПробовать: boolean;
  проба: ReturnType<typeof useПробаУведомлений>;
}) {
  const естьЖивой = список.some((устройство) => устройство.is_active);

  const естьЧтоПоказать = проба.подождать !== null || проба.отказ !== null || проба.итог !== null;

  return (
    // Отступы между частями — у самих частей (`mt-3`), а не зазором родителя: пустая живая
    // область внизу иначе добавляла бы к блоку лишние двенадцать точек под кнопкой.
    <div className='flex flex-col'>
      <ul aria-label='Телефоны человека' className='flex flex-col divide-y divide-stroke-soft-200'>
        {список.map((устройство) => (
          <СтрокаТелефона
            key={устройство.id}
            устройство={устройство}
            проверка={проба.итог?.данные.devices.find((итог) => итог.id === устройство.id)}
            // Когда живых нет, объяснение «слать некуда» стоит под списком и несёт то же самое
            // про всех: длинная фраза в каждой строке повторяла бы его два-три раза подряд.
            кратко={!естьЖивой}
          />
        ))}
      </ul>

      {!естьЖивой && (
        // Слать некуда, и кнопки нет вовсе — не серой, а именно нет: кнопка, которая обещает
        // пробу там, где пробовать нечего, сначала зовёт, а потом отвечает отказом.
        <div className='mt-3 flex flex-col gap-1'>
          <p className='text-label-sm text-text-strong-950'>Все телефоны отцеплены: слать некуда</p>
          <p className='text-paragraph-sm text-text-sub-600'>
            Человеку нужно открыть «Барк» на телефоне. Если уведомления не заработали, пусть выйдет
            из приложения и войдёт снова: адрес отправится заново.
          </p>
        </div>
      )}

      {естьЖивой && !можноПробовать && (
        // Объясняющая блокировка, а не пропавшая кнопка (правило 10): человек видит, что
        // проверка существует, кто её делает и к кому идти.
        <p className='mt-3 text-paragraph-sm text-text-sub-600'>
          Проверять уведомления могут администратор и руководство: обратитесь к ним.
        </p>
      )}

      {естьЖивой && можноПробовать && (
        <div className='mt-3'>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={проба.запустить}
            // Занятая кнопка ОСТАЁТСЯ кнопкой, а не становится `disabled`. Замер 01.10.2026 в
            // Chromium, то есть в движке десктопа на Windows: выключенная кнопка отдаёт фокус
            // странице, и после ответа человек с клавиатуры оказывается в начале вкладки — весь
            // путь табом заново. В WebKit фокус остаётся, поэтому глазами на маке это не видно.
            // Второй запрос отсекает сам запуск (`useПробаУведомлений`), вид «занято» даёт
            // атрибут, а объяснение, почему кнопка не отвечает, — слово «Отправляем…» на ней.
            aria-disabled={проба.идёт}
            aria-busy={проба.идёт}
            className='aria-disabled:pointer-events-none aria-disabled:bg-bg-weak-50 aria-disabled:text-text-disabled-300 aria-disabled:ring-transparent'
          >
            <Button.Icon as={RiNotification3Line} />
            {проба.идёт ? 'Отправляем…' : 'Проверить уведомления'}
          </Button.Root>
        </div>
      )}

      {/* Живая область стоит в разметке ВСЕГДА, а наполняется по ходу: читалка с экрана
          объявляет изменение только в области, которая уже была на странице. Вставленная
          вместе с итогом, она молчит, и слепому администратору проба выглядит как
          нажатие в пустоту. */}
      <div role='status' aria-live='polite'>
        {естьЧтоПоказать && (
          <div className='mt-3 flex flex-col gap-2'>
            {проба.подождать !== null && <Подождать слова={проба.подождать} />}
            {проба.отказ !== null && <ErrorAlert error={проба.отказ} />}
            {проба.итог !== null && <ИтогПробы итог={проба.итог} устарел={проба.идёт} />}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════
// Строка телефона
// ══════════════════════════════════════════════════════════════════════════════════════════

function СтрокаТелефона({
  устройство,
  проверка,
  кратко,
}: {
  устройство: DeviceOut;
  проверка: DeviceTestItem | undefined;
  кратко: boolean;
}) {
  const Значок = значокПлатформы(устройство.platform);
  const имя = имяУстройства(устройство);
  const состояние = состояниеСтроки(устройство, проверка, кратко);
  const Отметка = ОТМЕТКИ[состояние.тон].значок;

  return (
    <li className='flex gap-3 py-3 first:pt-0 last:pb-0'>
      <Значок aria-hidden className='mt-0.5 size-5 shrink-0 text-text-sub-600' />
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <p className='text-paragraph-sm text-text-sub-600'>
          <span className='text-label-sm text-text-strong-950'>
            {словоПлатформы(устройство.platform)}
          </span>
          {' · '}
          {словаВерсии(устройство.app_version)}
        </p>
        {/* Имя показывается, когда оно что-то добавляет: телефон, назвавший себя «iPhone»,
            под заголовком «iPhone» не повторяется. */}
        {имя !== null && <p className='text-paragraph-xs text-text-sub-600'>{имя}</p>}
        <p className='text-paragraph-xs text-text-sub-600'>
          {когдаСообщилАдрес(устройство.last_seen_at)}
        </p>
        <p className='flex items-start gap-1.5 text-paragraph-sm text-text-strong-950'>
          {/* Цвет ничего не решает в одиночку: состояние названо словами, а значок стоит
              рядом как второй признак. */}
          <Отметка
            aria-hidden
            className={cn('mt-0.5 size-4 shrink-0', ОТМЕТКИ[состояние.тон].цвет)}
          />
          <span>{состояние.слова}</span>
        </p>
      </div>
    </li>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════
// Итог пробы
// ══════════════════════════════════════════════════════════════════════════════════════════

/**
 * Просьба подождать: слова приходят ИЗ конверта, будь то ответ сервера или тот же конверт,
 * собранный на месте, когда запрос не ушёл. Цвет внимания, а не ошибки: человек ничего не
 * сломал, ему просто рано, и красная плашка читалась бы как отказ проверки.
 */
function Подождать({ слова }: { слова: { message: string; hint?: string; next_action?: string } }) {
  return (
    <Alert.Root variant='light' status='warning' size='small'>
      <Alert.Icon as={RiAlertLine} />
      <div className='flex min-w-0 flex-col gap-0.5'>
        <span>{слова.message}</span>
        {слова.hint !== undefined && <span className='text-paragraph-xs'>{слова.hint}</span>}
        {слова.next_action !== undefined && (
          <span className='text-paragraph-xs'>{слова.next_action}</span>
        )}
      </div>
    </Alert.Root>
  );
}

function ИтогПробы({ итог, устарел }: { итог: УдачнаяПроба; устарел: boolean }) {
  const { summary, next_action, devices } = итог.данные;
  // Когда все телефоны отказали, сервер кладёт в «что делать» слова ПЕРВОГО отказа — те же,
  // что уже стоят в его плашке. Второй раз подряд одно и то же предложение — это не шаг, а эхо.
  const эхо =
    next_action !== null && devices.some((устройство) => устройство.message === next_action);

  return (
    // Пока идёт новая проба, прежняя остаётся на месте приглушённой: блок не схлопывается под
    // рукой, а время внизу («проверено в 14:32») не даёт принять старое за свежее.
    <div className={cn('flex flex-col gap-2', устарел && 'opacity-60')} aria-busy={устарел}>
      {/* Общая строка нужна, когда телефонов несколько: «отправлено на один из двух» не видно
          ни из одной плашки по отдельности. У единственного телефона она пересказывала бы его
          же плашку: «Отправлено на 1 устройство» над «Уведомление отправлено» — одно и то же
          предложение подряд, и самый частый случай выглядел бы как запинка. */}
      {devices.length !== 1 && <p className='text-label-sm text-text-strong-950'>{summary}</p>}

      {devices.length > 0 && (
        <ul className='flex flex-col gap-2'>
          {devices.map((устройство) => {
            const отметка = ОТМЕТКИ[тонРезультата(устройство.result)];
            return (
              <li key={устройство.id}>
                <Alert.Root variant='light' status={отметка.статус} size='small'>
                  <Alert.Icon as={отметка.значок} />
                  <div className='flex min-w-0 flex-col gap-0.5'>
                    <span className='text-label-sm'>
                      <span className='sr-only'>{отметка.слово}. </span>
                      {подписьУстройства(устройство)}
                    </span>
                    <span>{устройство.message}</span>
                  </div>
                </Alert.Root>
              </li>
            );
          })}
        </ul>
      )}

      {next_action !== null && !эхо && (
        <p className='text-paragraph-sm text-text-strong-950'>
          <span className='text-label-sm'>Что делать: </span>
          {next_action}
        </p>
      )}

      <p className='text-paragraph-xs text-text-sub-600'>
        Проверено в {formatTimeHM(new Date(итог.ушлаВ))}
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════
// Слова и цвета
// ══════════════════════════════════════════════════════════════════════════════════════════

type Тон = 'успех' | 'внимание' | 'ошибка' | 'нейтрально';

/**
 * Какого цвета исход. Закрытый список сервера: новый исход без строки здесь не проскочит
 * молча — тип `Record` не даст собрать. Незнакомое слово на бою (сервер обновили раньше кабинета)
 * красится как ошибка: лучше лишний раз насторожить, чем показать неизвестное спокойно.
 */
const ТОН_РЕЗУЛЬТАТА: Record<DeviceTestResult, Тон> = {
  sent: 'успех',
  // Внимание: причина обычно у человека и лечится его действием.
  inactive: 'внимание',
  address_outdated: 'внимание',
  rate_limited: 'внимание',
  network: 'внимание',
  // Ошибка: причина на стороне сервера или сборки, человек сам ничего не исправит.
  wrong_environment: 'ошибка',
  not_configured: 'ошибка',
  server_key_problem: 'ошибка',
  failed: 'ошибка',
  // Нейтрально: отказа нет, этот вид устройства просто не получает уведомлений.
  unsupported: 'нейтрально',
};

function тонРезультата(результат: DeviceTestResult): Тон {
  return ТОН_РЕЗУЛЬТАТА[результат] ?? 'ошибка';
}

interface Отметка {
  статус: 'success' | 'warning' | 'error' | 'feature';
  значок: RemixiconComponentType;
  /** Цвет значка в строке телефона: только семантические токены темы. */
  цвет: string;
  /** Для чтения с экрана: цвет его не передаёт, а значок без подписи — просто картинка. */
  слово: string;
}

const ОТМЕТКИ: Record<Тон, Отметка> = {
  успех: {
    статус: 'success',
    значок: RiCheckboxCircleLine,
    цвет: 'text-success-base',
    слово: 'Успешно',
  },
  внимание: {
    статус: 'warning',
    значок: RiAlertLine,
    цвет: 'text-warning-base',
    слово: 'Внимание',
  },
  ошибка: { статус: 'error', значок: RiErrorWarningLine, цвет: 'text-error-base', слово: 'Ошибка' },
  нейтрально: {
    статус: 'feature',
    значок: RiInformationLine,
    цвет: 'text-text-sub-600',
    слово: 'К сведению',
  },
};

/** Что строка телефона говорит о нём: по данным сервера и, если проба уже была, по ней. */
function состояниеСтроки(
  устройство: DeviceOut,
  проверка: DeviceTestItem | undefined,
  кратко: boolean,
): { слова: string; тон: Тон } {
  if (!устройство.is_active) {
    return {
      // Коротко — когда объяснение уже стоит рядом: под списком («слать некуда») либо ниже, в
      // итоге пробы, где сервер говорит про этот телефон своими словами. Длинная фраза здесь
      // пересказывала бы ту же причину вторым текстом.
      слова:
        кратко || проверка !== undefined
          ? 'Адрес отцеплён'
          : 'Адрес отцеплён: телефон не принимает уведомления, пока человек не откроет приложение или не войдёт в него заново',
      тон: 'внимание',
    };
  }
  if (проверка !== undefined && проверка.result !== 'sent') {
    const тон = тонРезультата(проверка.result);
    return {
      // «Не прошла» — про отказ. Вид устройства, который уведомлений не получает вовсе, ничего
      // не завалил: проверять там нечего, и сказано об этом в итоге ниже.
      слова:
        тон === 'нейтрально' ? 'Подробности проверки ниже' : 'Проверка не прошла: причина ниже',
      тон,
    };
  }
  return { слова: 'Принимает уведомления', тон: 'успех' };
}

const СЛОВА_ПЛАТФОРМЫ: Readonly<Record<string, string>> = {
  ios: 'iPhone',
  android: 'Android',
  web: 'Браузер',
};

const ЗНАЧКИ_ПЛАТФОРМЫ: Readonly<Record<string, RemixiconComponentType>> = {
  ios: RiAppleFill,
  android: RiAndroidFill,
  web: RiGlobalLine,
};

/** Платформа словами. Незнакомая — «Устройство»: код вроде `harmony` человеку ни о чём. */
export function словоПлатформы(платформа: string): string {
  return СЛОВА_ПЛАТФОРМЫ[платформа] ?? 'Устройство';
}

function значокПлатформы(платформа: string): RemixiconComponentType {
  return ЗНАЧКИ_ПЛАТФОРМЫ[платформа] ?? RiSmartphoneLine;
}

/** «Барк 1.0.1», а без версии — прямо так и сказано: неизвестное не прячется. */
export function словаВерсии(версия: string | null): string {
  const чистая = версия?.trim() ?? '';
  return чистая === '' ? 'версия неизвестна' : `Барк ${чистая}`;
}

/** Имя, которое телефон дал себе, — если оно добавляет к слову платформы хоть что-то. */
function имяУстройства(устройство: DeviceOut): string | null {
  const имя = устройство.device_name?.trim() ?? '';
  if (имя === '') {
    return null;
  }
  return имя.toLowerCase() === словоПлатформы(устройство.platform).toLowerCase() ? null : имя;
}

/** Как назвать телефон в итоге пробы, где строки телефонов рядом нет: «Android · samsung SM-A546E». */
export function подписьУстройства(устройство: DeviceOut): string {
  const версия = устройство.app_version?.trim() ?? '';
  return [
    словоПлатформы(устройство.platform),
    имяУстройства(устройство),
    версия === '' ? null : `Барк ${версия}`,
  ]
    .filter((часть): часть is string => часть !== null)
    .join(' · ');
}

const МЕСЯЦЫ: readonly string[] = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

/** Сколько календарных дней назад. Деление с округлением: в сутки перехода на летнее время их 23 или 25 часов. */
function днейНазад(момент: Date, сейчас: Date): number {
  const началоДня = (день: Date) =>
    new Date(день.getFullYear(), день.getMonth(), день.getDate()).getTime();
  return Math.round((началоДня(сейчас) - началоДня(момент)) / 86_400_000);
}

/**
 * Когда телефон сообщил «Барку» свой адрес, по-человечески: «сегодня в 09:41», «вчера в
 * 18:05», «28 сентября». Часовой пояс — тот, в котором открыт кабинет, как и везде в продукте.
 *
 * Время у давних дат не пишется: «28 сентября в 09:41» про событие двухнедельной давности
 * читается как точность, которой для вывода «давно молчит» не нужно.
 */
export function когдаСообщилАдрес(значение: string, сейчас: Date = new Date()): string {
  const момент = parseMoment(значение);
  if (момент === null) {
    // Неизвестное называется неизвестным (правило 7), а не прочерком и не нулём.
    return 'Время, когда телефон сообщил адрес, не записано';
  }
  const дней = днейНазад(момент, сейчас);
  if (дней <= 0) {
    return `Сообщил адрес сегодня в ${formatTimeHM(момент)}`;
  }
  if (дней === 1) {
    return `Сообщил адрес вчера в ${formatTimeHM(момент)}`;
  }
  const год = момент.getFullYear() === сейчас.getFullYear() ? '' : ` ${момент.getFullYear()}`;
  return `Сообщил адрес ${момент.getDate()} ${МЕСЯЦЫ[момент.getMonth()] ?? ''}${год}`;
}
